import i18n from 'i18next';
import {Button, getFilePreviewUrl, openDialog, showNotification} from 'platform/components';
import {HStack, Link, Show, VStack} from 'platform/foundation';

import {MouseEvent} from 'react';

import {isNil} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {openFile} from 'shared/web';

import {CellRendererProps} from '../../types/CellRendererProps';
import {EmptyCellValue} from '../EmptyCellValue/EmptyCellValue';
import {getFileCellRendererIcon} from './utils/getFileCellRendererIcon';

type FileEntry = {
  id?: string;
  title?: string;
  remoteUrl?: string;
  contentType?: string;
};

type FileCellRendererProps = CellRendererProps<unknown>;

const handleOnClick = (event: MouseEvent, value: FileEntry) => {
  event.preventDefault();
  event.stopPropagation();

  if (!value.remoteUrl) {
    showNotification.error();
    return;
  }

  const url = getFilePreviewUrl(value.remoteUrl);

  if (isNil(url)) {
    showNotification.error();
    return;
  }

  openFile(url);
};

export function FileCellRenderer(props: FileCellRendererProps) {
  const cellValue = props.value as FileEntry | FileEntry[] | undefined;

  // No file at all: render the empty-cell placeholder instead of an inert
  // (previously error-notifying) empty link.
  if (isNil(cellValue) || (isArray(cellValue) && cellValue.length === 0)) {
    return <EmptyCellValue emptyCellValue={props.emptyCellValue} isWrap={props.isWrap} />;
  }

  if (isArray(cellValue)) {
    const firstValue = cellValue?.[0];

    return (
      <HStack align="center" spacing={2}>
        <Show when={cellValue?.length === 1}>
          <Link
            leftIcon={
              firstValue?.contentType ? getFileCellRendererIcon(firstValue?.contentType) : undefined
            }
            title={firstValue?.title}
            onClick={(event) => handleOnClick(event, firstValue)}
            size="small"
            download
          />
        </Show>

        <Show when={cellValue?.length > 1}>
          <Button
            variant="link"
            leftIcon="view"
            title={i18n.t('page.datagrid.labels.showFiles', {count: cellValue?.length ?? 0})}
            onClick={(event) => {
              event.stopPropagation();
              openDialog(
                <VStack spacing={2}>
                  {cellValue?.map((file, index) => (
                    <Link
                      title={file?.title}
                      onClick={(event) => handleOnClick(event, file)}
                      leftIcon={
                        file?.contentType ? getFileCellRendererIcon(file?.contentType) : undefined
                      }
                      size="small"
                      key={file?.id ?? index}
                    />
                  ))}
                </VStack>,
                {title: i18n.t('page.datagrid.labels.allFiles'), size: 'small'}
              );
            }}
          />
        </Show>
      </HStack>
    );
  }

  const singleValue = cellValue as FileEntry | undefined;

  return (
    <Link
      title={singleValue?.title}
      leftIcon={
        singleValue?.contentType ? getFileCellRendererIcon(singleValue?.contentType) : undefined
      }
      onClick={(event) => handleOnClick(event, singleValue ?? {})}
      size="small"
      download
    />
  );
}
